import React, { useMemo, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { useGlobalState, useToasts } from 'Context';
import Banner from 'v3/Components/Shared/Banner';
import { LinkButton, PrimaryButton, SecondaryButton } from 'v3/Components/Shared/Buttons';
import { ConfirmDialog } from 'v3/Components/Shared/ConfirmDialog';
import Tag, { TagColor } from 'v3/Components/Shared/Tag';
import './ReviewerImport.scss';

// Hidden admin page for the STRAD2-2591 reviewer spreadsheet import. Not linked from any menu;
// reachable at ROUTES.REVIEWER_IMPORT by users holding REVIEWER.PROFILE.IMPORT.
//
// Flow: choose file -> Validate (dry run, writes nothing) -> Import (enabled only after a clean
// validation of that same file). The backend re-validates on Import and rejects the whole file if
// anything changed in between, so the confirmation step never commits a partial import.

type ImportRowResult = {
  sheet: string;
  rowNumber: number;
  email?: string;
  name?: string;
  openCases: number;
  action: string;
  changes: string[];
  errors: string[];
  warnings: string[];
};

type ImportSummary = {
  reviewersUpdated: number;
  reviewersUnchanged: number;
  rowsSkipped: number;
  stateLicensesAdded: number;
  boardCertificationsAdded: number;
  imeLocationsAdded: number;
  reviewersArchived: number;
  reviewersDiscarded: number;
  reviewersWithOpenCases: number;
  rowsWithErrors: number;
  rowsWithWarnings: number;
};

type ImportResult = {
  dryRun: boolean;
  committed: boolean;
  hasErrors: boolean;
  fileErrors: string[];
  summary: ImportSummary;
  rows: ImportRowResult[];
};

// Mirrors ImportRowActions in ImportReviewersCommand.cs.
const ACTION_COLORS: Record<string, TagColor> = {
  Update: 'blue',
  'No changes': 'grey',
  Skip: 'yellow',
  Add: 'teal',
  'Already exists': 'grey',
  Invalid: 'red',
  Archive: 'orange',
  Discard: 'red',
};

// Sheet names as returned by the API (ReviewerImportTemplate / ImportReviewersCommandHandler.NotInFileSheet).
const SHEET = {
  reviewers: 'Reviewers',
  licenses: 'State Licenses',
  certifications: 'Board Certifications',
  imeLocations: 'IME Locations',
  notInFile: 'Not in spreadsheet',
};

type SummaryTileDef = {
  key: string;
  label: string;
  value: number;
  tone?: 'error' | 'warning';
  // Must select exactly the rows the backend counted for this tile (BuildSummary).
  matches: (row: ImportRowResult) => boolean;
  // When set, the Details column shows only this kind of message while the tile is selected.
  detail?: DetailKind;
};

type DetailKind = 'errors' | 'warnings' | 'openCases';

const buildTiles = (summary: ImportSummary, includeNotInFile: boolean): SummaryTileDef[] => [
  { key: 'updated', label: 'Reviewers updated', value: summary.reviewersUpdated,
    matches: (r) => r.sheet === SHEET.reviewers && r.action === 'Update' },
  { key: 'unchanged', label: 'Reviewers unchanged', value: summary.reviewersUnchanged,
    matches: (r) => r.sheet === SHEET.reviewers && r.action === 'No changes' },
  { key: 'licenses', label: 'State licenses added', value: summary.stateLicensesAdded,
    matches: (r) => r.sheet === SHEET.licenses && r.action === 'Add' },
  { key: 'certifications', label: 'Certifications added', value: summary.boardCertificationsAdded,
    matches: (r) => r.sheet === SHEET.certifications && r.action === 'Add' },
  { key: 'imeLocations', label: 'IME locations added', value: summary.imeLocationsAdded,
    matches: (r) => r.sheet === SHEET.imeLocations && r.action === 'Add' },
  ...(includeNotInFile
    ? [
        { key: 'archived', label: 'Not in file - archived', value: summary.reviewersArchived, tone: 'warning' as const,
          matches: (r: ImportRowResult) => r.sheet === SHEET.notInFile && r.action === 'Archive' },
        { key: 'discarded', label: 'Not in file - discarded', value: summary.reviewersDiscarded, tone: 'error' as const,
          matches: (r: ImportRowResult) => r.sheet === SHEET.notInFile && r.action === 'Discard' },
        { key: 'openCases', label: 'Not in file - open cases', value: summary.reviewersWithOpenCases, tone: 'error' as const, detail: 'openCases' as const,
          matches: (r: ImportRowResult) => r.sheet === SHEET.notInFile && r.openCases > 0 },
      ]
    : []),
  { key: 'skipped', label: 'Rows skipped', value: summary.rowsSkipped,
    matches: (r) => r.action === 'Skip' },
  { key: 'errors', label: 'Rows with errors', value: summary.rowsWithErrors, tone: 'error', detail: 'errors',
    matches: (r) => r.errors.length > 0 },
  { key: 'warnings', label: 'Rows with warnings', value: summary.rowsWithWarnings, tone: 'warning', detail: 'warnings',
    matches: (r) => r.warnings.length > 0 },
];

type ImportOptions = {
  processReviewersNotInFile: boolean;
  confirmArchiveWithOpenCases: boolean;
};

// A validation only unlocks Import for the exact file and "not in spreadsheet" choice it ran with.
const validationKey = (file: File, processReviewersNotInFile: boolean) =>
  `${file.name}|${file.size}|${file.lastModified}|${processReviewersNotInFile}`;

const hasWorkToDo = (summary: ImportSummary) =>
  summary.reviewersUpdated +
    summary.stateLicensesAdded +
    summary.boardCertificationsAdded +
    summary.imeLocationsAdded +
    summary.reviewersArchived +
    summary.reviewersDiscarded >
  0;

// Values come from an uploaded spreadsheet, so anything Excel would read as a formula is prefixed
// with an apostrophe (CSV injection), then the field is quoted.
const csvField = (value: string | number | undefined) => {
  let text = value === undefined || value === null ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

const exportRowsToCsv = (rows: ImportRowResult[], fileLabel: string) => {
  const header = ['Tab', 'Row', 'Reviewer', 'Email', 'Result', 'Open cases', 'Errors', 'Warnings', 'Changes'];
  const lines = rows.map((r) =>
    [
      r.sheet,
      r.rowNumber || '',
      r.name ?? '',
      r.email ?? '',
      r.action,
      r.openCases || '',
      r.errors.join(' | '),
      r.warnings.join(' | '),
      r.changes.join(' | '),
    ]
      .map(csvField)
      .join(',')
  );

  // BOM so Excel opens the file as UTF-8.
  const blob = new Blob(['\uFEFF' + [header.map(csvField).join(','), ...lines].join('\r\n')], {
    type: 'text/csv;charset=utf-8',
  });
  const ts = new Date().toISOString().replace(/[-T:.Z]/g, '').slice(0, 14);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ReviewerImport_${fileLabel}_${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const ReviewerImport: React.FC = () => {
  const { baseUrl, token } = useGlobalState();
  const toast = useToasts();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [validatedKey, setValidatedKey] = useState<string | null>(null);
  const [busy, setBusy] = useState<'validate' | 'import' | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [activeTileKey, setActiveTileKey] = useState<string | null>(null);
  const [processNotInFile, setProcessNotInFile] = useState(false);
  const [openCasesReviewed, setOpenCasesReviewed] = useState(false);
  const [typedCount, setTypedCount] = useState('');

  const removalCount = (result?.summary.reviewersArchived ?? 0) + (result?.summary.reviewersDiscarded ?? 0);
  const needsOpenCasesReview = (result?.summary.reviewersWithOpenCases ?? 0) > 0;
  const canImport =
    !!file &&
    !!result &&
    result.dryRun &&
    !result.hasErrors &&
    validatedKey === validationKey(file, processNotInFile) &&
    hasWorkToDo(result.summary) &&
    (!needsOpenCasesReview || openCasesReviewed) &&
    busy === null;

  // Archiving/discarding is confirmed by typing how many reviewers it affects.
  const confirmEnabled = removalCount === 0 || typedCount.trim() === String(removalCount);

  const tiles = useMemo(
    () => (result ? buildTiles(result.summary, processNotInFile) : []),
    [result, processNotInFile]
  );
  const activeTile = tiles.find((t) => t.key === activeTileKey);

  // A selected tile and "only issues" are alternative filters; picking one clears the other.
  const visibleRows = useMemo(() => {
    const rows = result?.rows ?? [];
    if (activeTile) return rows.filter(activeTile.matches);
    return rows.filter((r) => !onlyIssues || r.errors.length > 0 || r.warnings.length > 0);
  }, [result, onlyIssues, activeTile]);

  const toggleTile = (key: string) => {
    setActiveTileKey((current) => (current === key ? null : key));
    setOnlyIssues(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!selected) return;

    if (!selected.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Only .xlsx files are supported.');
      return;
    }

    setFile(selected);
    resetValidation();
  };

  const resetValidation = () => {
    setActiveTileKey(null);
    setResult(null);
    setValidatedKey(null);
    setOpenCasesReviewed(false);
  };

  // fetch rather than the axios API worker: the worker's instance headers force
  // Content-Type: application/json, which breaks the multipart boundary.
  const send = async (dryRun: boolean, options: ImportOptions): Promise<ImportResult | null> => {
    if (!file) return null;

    const formData = new FormData();
    formData.append('file', file, file.name);

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'x-api-version': '3.0',
    };
    const sessionId = localStorage.getItem('sessionId');
    if (sessionId) headers['X-Session-Id'] = sessionId;

    try {
      const response = await fetch(
        `${baseUrl}api/reviewers/import?dryRun=${dryRun}` +
          `&processReviewersNotInFile=${options.processReviewersNotInFile}` +
          `&confirmArchiveWithOpenCases=${options.confirmArchiveWithOpenCases}`,
        { method: 'POST', headers, body: formData }
      );

      if (response.status === 403) {
        toast.error('You do not have permission to import reviewers.');
        return null;
      }

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        toast.error(body?.message ?? 'The import request failed. Please try again.');
        return null;
      }

      return (await response.json()) as ImportResult;
    } catch {
      toast.error('The import request failed. Please try again.');
      return null;
    }
  };

  const handleValidate = async () => {
    if (!file || busy) return;
    setBusy('validate');
    const validation = await send(true, {
      processReviewersNotInFile: processNotInFile,
      confirmArchiveWithOpenCases: false,
    });
    setBusy(null);
    if (!validation) return;

    setResult(validation);
    setValidatedKey(validationKey(file, processNotInFile));
    setOpenCasesReviewed(false);
    setOnlyIssues(validation.hasErrors);
    setActiveTileKey(null);
  };

  const handleImport = async () => {
    // ConfirmDialog's disabled state is visual only, so the typed-count check is enforced here too.
    if (!confirmEnabled) return;
    setConfirmOpen(false);
    if (!canImport) return;

    setBusy('import');
    const imported = await send(false, {
      processReviewersNotInFile: processNotInFile,
      confirmArchiveWithOpenCases: openCasesReviewed,
    });
    setBusy(null);
    if (!imported) return;

    setResult(imported);
    setValidatedKey(null);
    setActiveTileKey(null);

    if (imported.committed) toast.success('Reviewer import completed.');
    else toast.error('Nothing was imported. Review the errors below and validate again.');
  };

  const summary = result?.summary;

  return (
    <div className="reviewer-import">
      <div className="reviewer-import__header">
        <h1 className="reviewer-import__title">Reviewer Import</h1>
      </div>

      <div className="reviewer-import__content">
        <section className="reviewer-import__card">
          <p className="reviewer-import__intro">
            Upload the completed reviewer import spreadsheet to complete existing reviewer
            profiles. <strong>Validate</strong>{' '}
            checks the whole file and shows what would change without saving anything.{' '}
            <strong>Import</strong> becomes available once the file validates with no errors.
          </p>
          <ul className="reviewer-import__rules">
            <li>
              Rows are matched to existing reviewers by email. Every email must already belong to a
              reviewer in Stradix - add new reviewers with the New Reviewer button.
            </li>
            <li>
              Reviewers are only filled in: blank fields are set, values already in Stradix are
              never overwritten, and their status does not change. No emails are sent.
            </li>
            <li>If any row has an error, nothing in the file is imported.</li>
            <li>Documents cannot be imported and must be uploaded on each profile.</li>
          </ul>

          <label className="reviewer-import__option">
            <input
              type="checkbox"
              checked={processNotInFile}
              disabled={busy !== null}
              onChange={(e) => {
                setProcessNotInFile(e.target.checked);
                resetValidation();
              }}
            />
            <span>
              <strong>Also process reviewers that are not in this spreadsheet.</strong> Reviewers
              that ever had a user account are archived; reviewers that never had one are
              discarded (permanently deleted). Only use this for the full V2 reviewer list -
              reviewers recently added with the New Reviewer button would be discarded too.
            </span>
          </label>

          <div className="reviewer-import__actions">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="reviewer-import__file-input"
              onChange={handleFileChange}
            />
            <SecondaryButton
              title={file ? 'Choose a different file' : 'Choose file'}
              disabled={busy !== null}
              onClick={() => busy === null && fileInputRef.current?.click()}
            />
            <span className="reviewer-import__file-name">
              {file ? file.name : 'No file selected'}
            </span>
            <div className="reviewer-import__spacer" />
            {busy && <CircularProgress size={20} />}
            <SecondaryButton
              title="Validate"
              disabled={!file || busy !== null}
              onClick={handleValidate}
            />
            <PrimaryButton
              title="Import"
              disabled={!canImport}
              onClick={() => {
                if (!canImport) return;
                setTypedCount('');
                setConfirmOpen(true);
              }}
            />
          </div>
        </section>

        {result && summary && (
          <section className="reviewer-import__card">
            <ResultBanner result={result} />

            {result.fileErrors.length > 0 && (
              <ul className="reviewer-import__file-errors">
                {result.fileErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            )}

            {result.rows.length > 0 && (
              <>
                <div className="reviewer-import__summary">
                  {tiles.map((tile) => (
                    <SummaryTile
                      key={tile.key}
                      tile={tile}
                      active={tile.key === activeTileKey}
                      onClick={() => toggleTile(tile.key)}
                    />
                  ))}
                </div>

                {result.dryRun && needsOpenCasesReview && (
                  <label className="reviewer-import__option reviewer-import__option--danger">
                    <input
                      type="checkbox"
                      checked={openCasesReviewed}
                      onChange={(e) => setOpenCasesReviewed(e.target.checked)}
                    />
                    <span>
                      I reviewed the {summary.reviewersWithOpenCases} reviewer(s) not in the
                      spreadsheet that still have open cases, and they should be archived.
                    </span>
                  </label>
                )}

                <div className="reviewer-import__filter-bar">
                  <label className="reviewer-import__filter">
                    <input
                      type="checkbox"
                      checked={onlyIssues}
                      onChange={(e) => {
                        setOnlyIssues(e.target.checked);
                        setActiveTileKey(null);
                      }}
                    />
                    Only show rows with errors or warnings
                  </label>
                  <span className="reviewer-import__muted">
                    Showing {visibleRows.length} of {result.rows.length} rows
                    {activeTile && <> - filtered by <strong>{activeTile.label}</strong></>}
                  </span>
                  {activeTile && (
                    <LinkButton title="Clear filter" onClick={() => setActiveTileKey(null)} />
                  )}
                  <div className="reviewer-import__spacer" />
                  <SecondaryButton
                    title={`Export ${visibleRows.length} row(s) to CSV`}
                    disabled={visibleRows.length === 0}
                    onClick={() =>
                      visibleRows.length > 0 &&
                      exportRowsToCsv(
                        visibleRows,
                        [
                          result.dryRun ? 'Validation' : 'Import',
                          activeTile?.label.replace(/[^A-Za-z0-9]+/g, '') ??
                            (onlyIssues ? 'ErrorsAndWarnings' : 'AllRows'),
                        ].join('_')
                      )
                    }
                  />
                </div>

                <div className="reviewer-import__table-wrapper">
                  <table className="reviewer-import__table">
                    <thead>
                      <tr>
                        <th>Tab</th>
                        <th>Row</th>
                        <th>Email</th>
                        <th>Result</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.map((row, index) => (
                        // Rows not in the spreadsheet have no row number, so the index keeps keys unique.
                        <tr key={`${row.sheet}-${row.rowNumber}-${row.email ?? ''}-${index}`}>
                          <td className="reviewer-import__nowrap">{row.sheet}</td>
                          <td>{row.rowNumber || '-'}</td>
                          <td className="reviewer-import__email">
                            {row.name && <div className="reviewer-import__name">{row.name}</div>}
                            {row.email ?? '-'}
                          </td>
                          <td>
                            <Tag label={row.action} color={ACTION_COLORS[row.action] ?? 'grey'} />
                          </td>
                          <td>
                            <RowDetails row={row} only={activeTile?.detail} />
                          </td>
                        </tr>
                      ))}
                      {visibleRows.length === 0 && (
                        <tr>
                          <td colSpan={5} className="reviewer-import__empty">
                            No rows to show.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}
      </div>

      <ConfirmDialog
        dialogVisible={confirmOpen}
        toggleDialog={() => setConfirmOpen(false)}
        primaryButtonEnabled={confirmEnabled}
        title="Import reviewers?"
        primaryButtonText="Import"
        secondaryButtonText="Cancel"
        OnPrimaryButtonClick={handleImport}
        OnSecondaryButtonClick={() => setConfirmOpen(false)}
        width={560}
      >
        {summary && (
          <div className="reviewer-import__confirm">
            <p>This will save the following to Stradix:</p>
            <ul>
              <li>{summary.reviewersUpdated} reviewer(s) with blank fields filled in</li>
              <li>
                {summary.stateLicensesAdded} state license(s),{' '}
                {summary.boardCertificationsAdded} board certification(s) and{' '}
                {summary.imeLocationsAdded} IME location(s)
              </li>
              {removalCount > 0 && (
                <>
                  <li>
                    <strong>{summary.reviewersArchived} reviewer(s) not in the spreadsheet archived</strong>
                  </li>
                  <li>
                    <strong>
                      {summary.reviewersDiscarded} reviewer(s) not in the spreadsheet permanently deleted
                    </strong>
                  </li>
                </>
              )}
            </ul>
            <p>Imported data can only be changed afterwards from each reviewer profile.</p>
            {removalCount > 0 && (
              <label className="reviewer-import__confirm-count">
                Type <strong>{removalCount}</strong> to confirm archiving and discarding these reviewers:
                <input
                  value={typedCount}
                  onChange={(e) => setTypedCount(e.target.value)}
                  inputMode="numeric"
                />
              </label>
            )}
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
};

const ResultBanner: React.FC<{ result: ImportResult }> = ({ result }) => {
  if (result.hasErrors) {
    return (
      <Banner
        variant="error"
        title={result.dryRun ? 'Validation failed.' : 'Import failed.'}
        description="Nothing was saved. Fix the errors in the spreadsheet and validate again."
      />
    );
  }

  if (result.committed) {
    return <Banner variant="success" title="Import completed." description="All rows below were saved." />;
  }

  if (!hasWorkToDo(result.summary)) {
    return (
      <Banner
        variant="warning"
        title="Nothing to import."
        description="Every row already matches Stradix or was skipped."
      />
    );
  }

  return (
    <Banner
      variant="success"
      title="Validation passed."
      description="Nothing has been saved yet. Review the rows below, then click Import."
    />
  );
};

const SummaryTile: React.FC<{ tile: SummaryTileDef; active: boolean; onClick: () => void }> = ({
  tile,
  active,
  onClick,
}) => {
  const classes = [
    'reviewer-import__tile',
    tile.tone && tile.value > 0 ? `reviewer-import__tile--${tile.tone}` : '',
    active ? 'reviewer-import__tile--active' : '',
  ].join(' ');

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      disabled={tile.value === 0 && !active}
      aria-pressed={active}
      title={active ? 'Click to clear this filter' : 'Click to show only these rows'}
    >
      <span className="reviewer-import__tile-value">{tile.value}</span>
      <span className="reviewer-import__tile-label">{tile.label}</span>
    </button>
  );
};

const RowDetails: React.FC<{ row: ImportRowResult; only?: DetailKind }> = ({ row, only }) => {
  const showErrors = !only || only === 'errors';
  const showWarnings = !only || only === 'warnings';
  const showOpenCases = row.openCases > 0 && (!only || only === 'openCases');
  const showChanges = !only && row.changes.length > 0;

  // On a real run without confirmation the backend also reports open cases as an error; don't say it twice.
  const errors = row.errors.filter((m) => !(row.openCases > 0 && m.startsWith('Has ')));

  const hasContent =
    (showErrors && errors.length > 0) || (showWarnings && row.warnings.length > 0) || showOpenCases || showChanges;
  if (!hasContent) return <span className="reviewer-import__muted">-</span>;

  return (
    <ul className="reviewer-import__details">
      {showOpenCases && (
        <li className="reviewer-import__detail--open-cases">
          {row.openCases} open case(s) assigned. Review this reviewer before archiving.
        </li>
      )}
      {showErrors &&
        errors.map((message) => (
          <li key={`e-${message}`} className="reviewer-import__detail--error">
            {message}
          </li>
        ))}
      {showWarnings &&
        row.warnings.map((message) => (
          <li key={`w-${message}`} className="reviewer-import__detail--warning">
            {message}
          </li>
        ))}
      {showChanges && (
        <li className="reviewer-import__detail--change">
          {row.action === 'Update' ? 'Fills in: ' : ''}
          {row.changes.join(', ')}
        </li>
      )}
    </ul>
  );
};

export default ReviewerImport;
